import { useState, useEffect } from 'react';
import axios from 'axios';
import { formatDate, getTodayISO } from '../utils/dateUtils';
import DateFilter, { filterByDateRange } from '../components/DateFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QualityInspection() {
  const [pendingItems, setPendingItems] = useState([]);
  const [reworkItems, setReworkItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [filterVendor, setFilterVendor] = useState('');
  const [dateFilters, setDateFilters] = useState({ startDate: null, endDate: null });
  const [activeTab, setActiveTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [showInspectModal, setShowInspectModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [inspectionForm, setInspectionForm] = useState({
    inspection_date: getTodayISO(),
    inspected_quantity: '',
    approved_quantity: '',
    rejected_quantity: '',
    rework_quantity: '',
    rejection_reason: '',
    remarks: ''
  });

  useEffect(() => {
    fetchData();
  }, [filterVendor]);

  const fetchData = async () => {
    try {
      const [pendingRes, reworkRes, vendorsRes] = await Promise.all([
        axios.get(`${API}/jobwork/qc-pending`, {
          params: { vendor_id: filterVendor || undefined }
        }),
        axios.get(`${API}/jobwork/rework-tracking`, {
          params: { vendor_id: filterVendor || undefined }
        }),
        axios.get(`${API}/jobwork/vendors`)
      ]);
      setPendingItems(pendingRes.data);
      setReworkItems(reworkRes.data);
      setVendors(vendorsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openInspectModal = (item) => {
    setSelectedItem(item);
    setInspectionForm({
      inspection_date: getTodayISO(),
      inspected_quantity: item.pending_qc_quantity.toString(),
      approved_quantity: item.pending_qc_quantity.toString(),
      rejected_quantity: '0',
      rework_quantity: '0',
      rejection_reason: '',
      remarks: ''
    });
    setShowInspectModal(true);
  };

  const handleQuantityChange = (field, value) => {
    const numValue = parseInt(value) || 0;
    const inspected = parseInt(inspectionForm.inspected_quantity) || 0;
    
    let newForm = { ...inspectionForm, [field]: value };
    
    // Auto-calculate remaining quantities
    if (field === 'approved_quantity') {
      const remaining = inspected - numValue;
      if (remaining >= 0) {
        // Keep existing rejected/rework split or default to rejected
        const currentRejected = parseInt(newForm.rejected_quantity) || 0;
        const currentRework = parseInt(newForm.rework_quantity) || 0;
        if (currentRejected + currentRework !== remaining) {
          newForm.rejected_quantity = remaining.toString();
          newForm.rework_quantity = '0';
        }
      }
    } else if (field === 'rejected_quantity') {
      const approved = parseInt(newForm.approved_quantity) || 0;
      newForm.rework_quantity = Math.max(0, inspected - approved - numValue).toString();
    } else if (field === 'rework_quantity') {
      const approved = parseInt(newForm.approved_quantity) || 0;
      newForm.rejected_quantity = Math.max(0, inspected - approved - numValue).toString();
    } else if (field === 'inspected_quantity') {
      // Reset all to inspected when changing total
      newForm.approved_quantity = value;
      newForm.rejected_quantity = '0';
      newForm.rework_quantity = '0';
    }
    
    setInspectionForm(newForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const inspected = parseInt(inspectionForm.inspected_quantity) || 0;
    const approved = parseInt(inspectionForm.approved_quantity) || 0;
    const rejected = parseInt(inspectionForm.rejected_quantity) || 0;
    const rework = parseInt(inspectionForm.rework_quantity) || 0;
    
    if (approved + rejected + rework !== inspected) {
      alert('Approved + Rejected + Rework must equal Inspected quantity');
      return;
    }
    
    try {
      await axios.post(`${API}/jobwork/qc-inspection`, {
        challan_id: selectedItem.challan_id,
        receipt_id: selectedItem.receipt_id,
        items: [{
          partcode: selectedItem.partcode,
          description: selectedItem.description,
          inspected_quantity: inspected,
          approved_quantity: approved,
          rejected_quantity: rejected,
          rework_quantity: rework,
          rejection_reason: inspectionForm.rejection_reason || null,
          remarks: inspectionForm.remarks || null
        }],
        inspection_date: inspectionForm.inspection_date,
        remarks: inspectionForm.remarks || null
      });
      
      setShowInspectModal(false);
      fetchData();
    } catch (error) {
      console.error('Error recording inspection:', error);
      alert('Error recording inspection: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  // Apply date filter
  const filteredPendingItems = filterByDateRange(pendingItems, 'receipt_date', dateFilters.startDate, dateFilters.endDate);
  const filteredReworkItems = filterByDateRange(reworkItems, 'original_receipt_date', dateFilters.startDate, dateFilters.endDate);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Quality Inspection</h1>
          <p className="text-gray-600 mt-1">Inspect received material and track rework items</p>
        </div>
      </div>

      {/* Date Filter */}
      <DateFilter onFilterChange={setDateFilters} />

      {/* Tabs */}
      <div className="flex gap-4 border-b">
        <button
          onClick={() => setActiveTab('pending')}
          className={`pb-3 px-1 font-medium border-b-2 transition ${
            activeTab === 'pending' 
              ? 'border-red-600 text-red-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          🔍 Pending QC ({filteredPendingItems.length})
        </button>
        <button
          onClick={() => setActiveTab('rework')}
          className={`pb-3 px-1 font-medium border-b-2 transition ${
            activeTab === 'rework' 
              ? 'border-red-600 text-red-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          🔄 Rework Tracking ({filteredReworkItems.length})
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <select
          value={filterVendor}
          onChange={(e) => setFilterVendor(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg"
        >
          <option value="">All Vendors</option>
          {vendors.map(v => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
        </select>
      </div>

      {/* Pending QC Tab */}
      {activeTab === 'pending' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Challan</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Received Date</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Bill Qty</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actual Qty</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Inspected</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Pending QC</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pendingItems.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                    ✅ No items pending QC inspection
                  </td>
                </tr>
              ) : (
                filteredPendingItems.map((item, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">{item.challan_number}</td>
                    <td className="px-6 py-4 text-gray-600">{item.vendor_name}</td>
                    <td className="px-6 py-4">
                      <div className="font-medium">{item.partcode}</div>
                      <div className="text-sm text-gray-500">{item.description}</div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{formatDate(item.received_date)}</td>
                    <td className="px-6 py-4 text-center">{item.bill_quantity}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={item.actual_quantity !== item.bill_quantity ? 'text-orange-600 font-medium' : ''}>
                        {item.actual_quantity}
                      </span>
                      {item.actual_quantity !== item.bill_quantity && (
                        <span className="text-xs text-orange-500 block">
                          ({item.actual_quantity > item.bill_quantity ? '+' : ''}{item.actual_quantity - item.bill_quantity})
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center text-green-600">{item.inspected_quantity}</td>
                    <td className="px-6 py-4 text-center">
                      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-sm font-medium">
                        {item.pending_qc_quantity}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => openInspectModal(item)}
                        className="bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 text-sm"
                      >
                        Inspect
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Rework Tracking Tab */}
      {activeTab === 'rework' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Challan</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inspection Date</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Rework Qty</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {reworkItems.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    ✅ No items pending rework
                  </td>
                </tr>
              ) : (
                filteredReworkItems.map((item, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">{item.challan_number}</td>
                    <td className="px-6 py-4 text-gray-600">{item.vendor_name}</td>
                    <td className="px-6 py-4">
                      <div className="font-medium">{item.partcode}</div>
                      <div className="text-sm text-gray-500">{item.description}</div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{formatDate(item.inspection_date)}</td>
                    <td className="px-6 py-4 text-center">
                      <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-sm font-medium">
                        {item.rework_quantity}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{item.rejection_reason || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          
          {reworkItems.length > 0 && (
            <div className="p-4 bg-orange-50 border-t border-orange-200">
              <p className="text-sm text-orange-800">
                💡 <strong>Tip:</strong> When sending rework material back to vendor, create a new challan and mark those items as &quot;Rework&quot; type to avoid double counting.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Inspect Modal */}
      {showInspectModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-xl w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Quality Inspection</h2>
                  <p className="text-gray-600">{selectedItem.challan_number} - {selectedItem.partcode}</p>
                </div>
                <button onClick={() => setShowInspectModal(false)} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Item Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Part:</span>
                    <span className="ml-2 font-medium">{selectedItem.partcode}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Vendor:</span>
                    <span className="ml-2 font-medium">{selectedItem.vendor_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Bill Qty:</span>
                    <span className="ml-2 font-medium">{selectedItem.bill_quantity}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Actual Received:</span>
                    <span className={`ml-2 font-medium ${selectedItem.actual_quantity !== selectedItem.bill_quantity ? 'text-orange-600' : ''}`}>
                      {selectedItem.actual_quantity}
                    </span>
                  </div>
                </div>
              </div>

              {/* Inspection Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Inspection Date *</label>
                <input
                  type="date"
                  value={inspectionForm.inspection_date}
                  onChange={(e) => setInspectionForm({ ...inspectionForm, inspection_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>

              {/* Quantities */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Qty Inspected *</label>
                  <input
                    type="number"
                    min="1"
                    max={selectedItem.pending_qc_quantity}
                    value={inspectionForm.inspected_quantity}
                    onChange={(e) => handleQuantityChange('inspected_quantity', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-green-700 mb-1">✓ Approved *</label>
                  <input
                    type="number"
                    min="0"
                    value={inspectionForm.approved_quantity}
                    onChange={(e) => handleQuantityChange('approved_quantity', e.target.value)}
                    className="w-full px-3 py-2 border border-green-300 rounded-lg bg-green-50"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-red-700 mb-1">✗ Rejected</label>
                  <input
                    type="number"
                    min="0"
                    value={inspectionForm.rejected_quantity}
                    onChange={(e) => handleQuantityChange('rejected_quantity', e.target.value)}
                    className="w-full px-3 py-2 border border-red-300 rounded-lg bg-red-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-orange-700 mb-1">↻ Rework</label>
                  <input
                    type="number"
                    min="0"
                    value={inspectionForm.rework_quantity}
                    onChange={(e) => handleQuantityChange('rework_quantity', e.target.value)}
                    className="w-full px-3 py-2 border border-orange-300 rounded-lg bg-orange-50"
                  />
                </div>
              </div>

              {/* Validation message */}
              {(() => {
                const inspected = parseInt(inspectionForm.inspected_quantity) || 0;
                const approved = parseInt(inspectionForm.approved_quantity) || 0;
                const rejected = parseInt(inspectionForm.rejected_quantity) || 0;
                const rework = parseInt(inspectionForm.rework_quantity) || 0;
                const total = approved + rejected + rework;
                
                if (total !== inspected && inspected > 0) {
                  return (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                      ⚠️ Total (Approved + Rejected + Rework = {total}) must equal Inspected ({inspected})
                    </div>
                  );
                }
                return null;
              })()}

              {/* Rejection Reason */}
              {(parseInt(inspectionForm.rejected_quantity) > 0 || parseInt(inspectionForm.rework_quantity) > 0) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rejection/Rework Reason</label>
                  <input
                    type="text"
                    value={inspectionForm.rejection_reason}
                    onChange={(e) => setInspectionForm({ ...inspectionForm, rejection_reason: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., Dimensional error, Surface defect, etc."
                  />
                </div>
              )}

              {/* Remarks */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={inspectionForm.remarks}
                  onChange={(e) => setInspectionForm({ ...inspectionForm, remarks: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="2"
                  placeholder="Optional notes..."
                />
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowInspectModal(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Record Inspection
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
